import { AxiosResponse } from 'axios';

import { axiosAuthInstance } from 'api/base';

import { AUTH_REQUEST } from './constants';
import { normalizeSignIn } from './normalizers';
import type { IAuthState, ILoginPayload } from './types';
import { IPasswordRecoveryData, ISetPasswordData } from './types';

const authAPI = {
  async logout(_data?: boolean): Promise<AxiosResponse> {
    const result = await axiosAuthInstance.get('auth/logout', {
      authRequest: AUTH_REQUEST.LOGOUT,
      headers: { Authorization: undefined },
      withCredentials: true,
    });

    return result?.data;
  },

  async signIn(payload: ILoginPayload): Promise<IAuthState> {
    const result = await axiosAuthInstance.post('auth/login', payload, {
      withCredentials: true,
    });

    return normalizeSignIn(result?.data);
  },

  async refreshToken(): Promise<IAuthState> {
    const doRequest = async () => {
      const result = await axiosAuthInstance.get('auth/token/refresh', {
        authRequest: AUTH_REQUEST.REFRESH,
        headers: {
          Authorization: undefined,
        },
        withCredentials: true,
      });

      return normalizeSignIn(result?.data);
    };

    if (!('locks' in navigator)) {
      // Fallback if browser doesn't support Web Locks API
      return doRequest();
    }

    // Locks API prevents race condition of refresh token request across other tab if there are any
    // and ensures that only one request is made at a time
    return navigator.locks.request('refresh-token-lock', doRequest);
  },

  async passwordRecovery(payload: IPasswordRecoveryData): Promise<AxiosResponse> {
    const result = await axiosAuthInstance.post('users/password/initiate-reset', payload);

    return result?.data;
  },

  async setUserPassword(payload: ISetPasswordData): Promise<AxiosResponse> {
    const result = await axiosAuthInstance.post('users/password/confirm-reset', payload, {
      authRequest: AUTH_REQUEST.SET_PASSWORD,
    });

    return result?.data;
  },
};

export default authAPI;
